/* eslint-disable react/destructuring-assignment */
import React from 'react';
import { useQuery, useLazyQuery, useMutation, gql } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';

import { useUser } from 'actions/useUser';
import { Role } from 'types/schema.type';

import { LoadingSpinner } from '../LoadingSpinner';
import { DOWNLOAD_RECORDINGS_BATCH } from '../../mutations';
import { GridCellExpand } from '../datagrid/GridCellExpand';
import { GridCustomToolbar } from '../datagrid/GridCustomToolbar';
import { CustomFilterPanel, FilterRow } from '../datagrid/CustomFilterPanel';
import { buildRecordingsFilterInput } from './adminFilterInput';
import { ReanalyzeSelectedButton } from './reanalysis/ReanalyzeSelectedButton';

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 650,
  },
  container: {
    width: 1400,
    marginTop: theme.spacing(4),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  downloadButton: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
}));

const getIsoTime = (timestamp) => {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const isoTime = date.toISOString();

  return isoTime;
};

const stringifyFlags = (flags) => {
  if (!flags) return '';
  if (!flags.length) return '';

  return flags.join(', ');
  // return flags.join(',\n');
};

const makeRows = (items) =>
  items.map((item) => ({
    id: item.id,
    userEmail: item.user.email,
    userId: item.user.id,
    comment: item.comment,
    rPeaks: item.analysis?.rpeaksCount, // map from analysis field
    fileName: item.fileName,
    flags: stringifyFlags(item.flags),
    recordingStartedAt: getIsoTime(item.recordingStartedAt),
    uploadedAt: getIsoTime(item.uploadedAt),
    // project_management#514: WHEN the stored analysis was produced. Recording
    // analysis is bit-for-bit deterministic, so without this a successful
    // reanalysis changes nothing visible and reads as "it never ran".
    analysedAt: getIsoTime(item.analysis?.analysedAt),
  }));

function renderCellExpand(params) {
  return (
    <GridCellExpand
      value={params.value ? params.value.toString() : ''}
      width={params.colDef.computedWidth}
    />
  );
}

const columns = [
  { field: 'id', headerName: 'Recording ID', width: 220 },
  {
    field: 'userEmail',
    headerName: 'Email',
    width: 160,
    renderCell: renderCellExpand,
  },
  {
    field: 'userId',
    headerName: 'User ID',
    width: 220,
  },
  {
    field: 'comment',
    headerName: 'Comment',
    width: 160,
    renderCell: renderCellExpand,
  },
  {
    field: 'rPeaks',
    headerName: 'R-Peaks',
    width: 150,
    renderCell: renderCellExpand,
  },
  {
    field: 'fileName',
    headerName: 'File Name',
    width: 140,
  },
  {
    field: 'flags',
    headerName: 'Flags',
    width: 250,
    renderCell: renderCellExpand,
  },
  {
    field: 'recordingStartedAt',
    headerName: 'Recorded At',
    width: 200,
  },
  {
    field: 'uploadedAt',
    headerName: 'Uploaded At',
    width: 200,
  },
  {
    // The only visible proof that a reanalysis actually ran (#514).
    field: 'analysedAt',
    headerName: 'Analysed At',
    width: 200,
  },
];

export const GET_ALL_RECORDINGS_PAGINATED = gql`
  query GetAllRecordingsPaginated($offset: Int!, $limit: Int!, $filter: RecordingsFilterInput) {
    getAllRecordingsPaginated(offset: $offset, limit: $limit, filter: $filter) {
      success
      error
      totalCount
      recordings {
        id
        comment
        fileName
        flags
        recordingStartedAt
        uploadedAt
        analysis {
          id
          rpeaksCount
          analysedAt
        }
        user {
          id
          email
        }
      }
    }
  }
`;

const GET_ALL_RECORDING_IDS_PAGINATED = gql`
  query GetAllRecordingIdsPaginated($offset: Int!, $limit: Int!, $filter: RecordingsFilterInput) {
    getAllRecordingsPaginated(offset: $offset, limit: $limit, filter: $filter) {
      success
      error
      totalCount
      recordings {
        id
      }
    }
  }
`;

const MAX_SELECT_ALL = 5000;

export function RecordingManagement() {
  const classes = useStyles();
  const [selectedRecordingIds, setSelectedRecordingIds] = React.useState<string[]>([]);
  const [showDownloadButton, setShowDownloadButton] = React.useState(true);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const [metadataOnly, setMetadataOnly] = React.useState(false);

  // --- Custom filter state ---
  const [customFilters, setCustomFilters] = React.useState<FilterRow[]>([]);
  const [debouncedFilters, setDebouncedFilters] = React.useState<FilterRow[]>(customFilters);

  // Debounce filter updates
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilters(customFilters);
    }, 500); // 500ms debounce

    return () => clearTimeout(handler);
  }, [customFilters]);

  // --- Pagination State ---
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(100);
  const [rowCountState, setRowCountState] = React.useState(0);

  const { user, loading: authLoading } = useUser();

  // --- Convert custom filters to backend filter input ---
  // Rows on the same column are combined; list columns split on commas,
  // spaces and new lines (#575).
  const filterInput = buildRecordingsFilterInput(debouncedFilters);

  const { loading: dataLoading, error, data } = useQuery(GET_ALL_RECORDINGS_PAGINATED, {
    variables: {
      offset: page * pageSize,
      limit: pageSize,
      filter: Object.keys(filterInput).length ? filterInput : undefined,
    },
    notifyOnNetworkStatusChange: true,
  });

  const [
    downloadRecordingsBatch,
    { data: downloadData, loading: downloadLoading, error: downloadError },
  ] = useMutation(DOWNLOAD_RECORDINGS_BATCH);

  const [fetchAllMatching, { loading: selectAllLoading }] = useLazyQuery(
    GET_ALL_RECORDING_IDS_PAGINATED,
    {
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const ids = (res?.getAllRecordingsPaginated?.recordings ?? []).map((r) => r.id);

        setSelectedRecordingIds(ids);
      },
    },
  );

  const downlaodSuccess = downloadData?.downloadRecordingsBatch?.success;

  React.useEffect(() => {
    setRowCountState((prevRowCountState) =>
      data?.getAllRecordingsPaginated?.totalCount !== undefined
        ? data.getAllRecordingsPaginated.totalCount
        : prevRowCountState,
    );
  }, [data?.getAllRecordingsPaginated?.totalCount, setRowCountState]);

  React.useEffect(() => {
    if (downloadLoading) {
      setShowDownloadButton(false);
    }

    if (!downloadLoading && (downloadError || downlaodSuccess === false)) {
      setShowError(true);
    }

    if (downlaodSuccess) {
      setShowSuccess(true);
    }
  }, [downloadLoading, downloadError, downlaodSuccess]);

  // --- Reset page to 0 when filters change ---
  React.useEffect(() => {
    setPage(0);
  }, [debouncedFilters]);

  React.useEffect(() => {
    setSelectedRecordingIds([]);
  }, [debouncedFilters]);

  if (authLoading || !user) return <div>Loading auth...</div>;

  if (user.role !== Role.Admin) return null;

  if (error && !data?.getAllRecordingsPaginated?.recordings) {
    return <div>Error loading recordings: ${error.message}</div>;
  }

  const response = data?.getAllRecordingsPaginated;
  const recordings = response?.recordings ?? [];
  const rows = makeRows(recordings);

  // MUI v4 DataGrid strips non-visible ids from a controlled selectionModel, so
  // we keep two sets: the full selection (source of truth, can span pages) and
  // the visible subset we actually hand to the grid.
  const currentPageIds = rows.map((r) => r.id);
  const visibleSelection = selectedRecordingIds.filter((id) => currentPageIds.includes(id));

  const handleSelectChange = (nextVisible: string[]) => {
    const offPageIds = selectedRecordingIds.filter((id) => !currentPageIds.includes(id));

    setSelectedRecordingIds([...offPageIds, ...nextVisible]);
  };

  const handleDownload = () => {
    downloadRecordingsBatch({
      variables: { recordingIds: selectedRecordingIds, metadataOnly },
    });
  };

  const handleAlertClose = () => {
    setShowDownloadButton(true);
    setShowError(false);
    setShowSuccess(false);
  };

  const handleShowChart = () => {
    if (!selectedRecordingIds.length) return null;

    const query = selectedRecordingIds.join(',');

    return window.open(`/admin-dashboard/recording?id=${query}`, '_blank');
  };

  const hasActiveFilter = Object.keys(filterInput).length > 0;
  const tooManyMatches = rowCountState > MAX_SELECT_ALL;
  const selectAllDisabled =
    !hasActiveFilter || rowCountState === 0 || tooManyMatches || selectAllLoading;
  let selectAllTooltip = '';

  if (!hasActiveFilter) {
    selectAllTooltip = 'Add at least one filter to select across pages';
  } else if (tooManyMatches) {
    selectAllTooltip = `Too many results (${rowCountState}). Narrow filters to ≤${MAX_SELECT_ALL}.`;
  }

  return (
    <>
      <Container className={classes.container} maxWidth={false}>
        {showSuccess && (
          <Alert
            severity="success"
            action={
              <Button color="inherit" size="small" onClick={handleAlertClose}>
                CLOSE
              </Button>
            }
          >
            <AlertTitle>Success</AlertTitle>
            Download link will be sent to {user.email}
          </Alert>
        )}
        {showError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={handleAlertClose}>
                CLOSE
              </Button>
            }
          >
            <AlertTitle>Error</AlertTitle>
            {downloadData?.downloadRecordingsBatch?.error}
          </Alert>
        )}
        {downloadLoading && <LoadingSpinner />}
        {showDownloadButton && (
          <>
            <Button
              className={classes.downloadButton}
              variant="contained"
              color="primary"
              disabled={!selectedRecordingIds.length}
              onClick={handleDownload}
            >
              {selectedRecordingIds.length > 0
                ? `Download selected (${selectedRecordingIds.length})`
                : 'Download selected'}
            </Button>

            <Button
              className={classes.downloadButton}
              variant="outlined"
              color="primary"
              disabled={selectAllDisabled}
              title={selectAllTooltip}
              onClick={() => fetchAllMatching({
                variables: { offset: 0, limit: MAX_SELECT_ALL, filter: filterInput },
              })}
            >
              {selectAllLoading ? 'Selecting…' : `Select all ${rowCountState} matching`}
            </Button>

            <FormControlLabel
              className={classes.downloadButton}
              control={(
                <Checkbox
                  checked={metadataOnly}
                  color="primary"
                  onChange={(e) => setMetadataOnly(e.target.checked)}
                />
              )}
              label="Metadata only (no raw recordings)"
            />

            <Button
              className={classes.downloadButton}
              variant="contained"
              color="primary"
              disabled={!selectedRecordingIds.length}
              onClick={handleShowChart}
            >
              Open in new tab
            </Button>

            {/*
              project_management#514 — same selection, paced bulk reanalysis.
              #574: the selection may span patients (and pages, via "Select all
              N matching"); the server preview reads each recording's owner
              from its own record and asks to confirm a multi-patient batch.
            */}
            <ReanalyzeSelectedButton
              className={classes.downloadButton}
              scope="recording"
              selected={selectedRecordingIds.map((id) => ({ id }))}
            />
          </>
        )}
        <CustomFilterPanel
          filters={customFilters}
          onFiltersChange={setCustomFilters}
        />
        <div style={{ width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            components={{
              Toolbar: GridCustomToolbar,
            }}
            rowCount={rowCountState}
            loading={dataLoading}
            rowsPerPageOptions={[10, 25, 50, 100]}
            paginationMode="server"
            page={page}
            pageSize={pageSize}
            selectionModel={visibleSelection}
            pagination
            autoHeight
            checkboxSelection
            disableSelectionOnClick
            disableColumnFilter
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onSelectionModelChange={(newSelection) => handleSelectChange(newSelection as string[])}
          />
        </div>
      </Container>
    </>
  );
}
